import {
    useCallback,
    useMemo,
} from 'react'
import {
    Button,
    Dialog,
    DialogActions,
} from '@mui/material'
import {
    StaticDateTimePicker,
} from '@mui/x-date-pickers/StaticDateTimePicker'

import Select from 'app/shared-components/Select'
import {
    dateToStr,
    strToMoment,
    dateTimeMask,
    getTimezones,
} from 'app/utils/date'
import useEnumValues from 'app/hooks/useEnumValues'
import {
    Timezone,
} from 'app/enums/label.enums'

import ToolbarWithKeyboardViewSwitch from './components/ToolbarWithKeyboardViewSwitch'
import LayoutWithKeyboardView from './components/LayoutWithKeyboardView'

import styles from './Zone.module.css'

type Props = {
    state: {
        newZone: string,
        newDatetime: string,
    },
    setState: (state: any) => void,
    handleChangeZone: (zone: string) => void,
    handleChange: (datetime: string) => void,
    handleClose: () => void,
    disableFuture?: boolean,
    isDisableWeekends?: boolean,
}

const defaultProps: Partial<Props> = {
    disableFuture: false,
    isDisableWeekends: false,
}

const Zone = ({
    state,
    setState,
    handleChange,
    handleChangeZone,
    handleClose,
    disableFuture,
    isDisableWeekends,
}: Props) => {
    const timezonesAllowedValues = getTimezones(useEnumValues()(Timezone))

    const setDateAndZone = useCallback((datetime) => {
        const newDatetime = datetime ? dateToStr(datetime) : null

        setState({
            ...state,
            newDatetime,
        })
    }, [
        setState,
        state,
    ])

    const handleRemove = () => {
        handleChange(null)
        handleClose()
    }

    const handleSetTime = useCallback(() => {
        const dateWithoutZone = state.newDatetime.split(' ').slice(0, -1).join(' ')

        handleChange(`${dateWithoutZone} ${state.newZone}`)
        handleClose()
    }, [
        handleChange,
        handleClose,
        state.newDatetime,
        state.newZone,
    ])

    const datetime = useMemo(() => {
        return strToMoment(state.newDatetime, dateTimeMask)
    }, [state.newDatetime])

    function disableWeekends(date) {
        return date.day() === 0 || date.day() === 6
    }

    return (
        <Dialog
            open
            onClose={handleClose}
            aria-labelledby="form-dialog-title"
        >
            <>
                <StaticDateTimePicker
                    ampm={false}
                    value={datetime}
                    onChange={setDateAndZone}
                    disableFuture={disableFuture}
                    shouldDisableDate={isDisableWeekends ? disableWeekends : null}
                    slots={{
                        actionBar: () => {
                            return null
                        },
                        layout: LayoutWithKeyboardView,
                        toolbar: ToolbarWithKeyboardViewSwitch,
                    }}
                />
                <div className={styles.zones}>
                    {state.newZone !== '' && (
                        <Select
                            title="Time Zone"
                            data={timezonesAllowedValues}
                            value={state.newZone}
                            onChange={handleChangeZone}
                        />
                    )}
                </div>
                <DialogActions>
                    <Button
                        onClick={handleRemove}
                        color="primary"
                    >
                        Remove
                    </Button>
                    <Button
                        onClick={handleClose}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={!state.newDatetime}
                        onClick={handleSetTime}
                        color="primary"
                    >
                        Ok
                    </Button>
                </DialogActions>
            </>
        </Dialog>
    )
}

Zone.defaultProps = defaultProps

export default Zone
